import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ActionIcon, Box, Button, Group, Loader, Modal, Stack, Text, UnstyledButton, useComputedColorScheme } from '@mantine/core';
import { IconChevronLeft, IconChevronRight, IconPencil, IconPlus } from '@tabler/icons-react';
import ScenePreviewFrame from '../shell/studio/ScenePreviewFrame';
import { track } from '../../utils/mixpanel';
import { AnalyticsEvents } from '../../analytics/events';
import type {
    PlaylistSceneEntry,
    SceneTemplateId,
    ShellComponent,
} from '../../data/ShellConfig';

type Props = {
    opened: boolean;
    onClose: () => void;
    playlistName?: string;
    scenes: PlaylistSceneEntry[];
    components: ShellComponent[];
    /** Opens the scene editor for the given scene. */
    onEditScene: (sceneId: string) => void;
    /** When provided, a trailing "Add scene" slide appears after the last scene. */
    onAddScene?: () => void;
    /** Shows a "Updating…" overlay over the scene while a save revalidates. */
    updating?: boolean;
};

const SWIPE_THRESHOLD_PX = 40;

/**
 * Full-size, one-scene-at-a-time preview of a device's playlist. Navigable by
 * swipe on touch devices and by arrow buttons / keyboard on desktop.
 */
const PlaylistPreviewModal: React.FC<Props> = ({
    opened,
    onClose,
    playlistName,
    scenes,
    components,
    onEditScene,
    onAddScene,
    updating = false,
}) => {
    const [index, setIndex] = useState(0);
    const touchStartX = useRef<number | null>(null);
    const scheme = useComputedColorScheme('light');
    // The current dot should always read as the more prominent / darker one.
    // On dark bg that's the bright blue; on white it's a saturated blue against
    // a light-gray inactive dot (dark-3 was too dark on white).
    const activeDot =
        scheme === 'dark' ? 'var(--mantine-color-blue-5)' : 'var(--mantine-color-blue-6)';
    const inactiveDot =
        scheme === 'dark' ? 'var(--mantine-color-dark-3)' : 'var(--mantine-color-gray-4)';

    const count = scenes.length;
    // An extra terminal slot for the "Add scene" call-to-action, when enabled.
    const hasAddSlide = Boolean(onAddScene);
    const slotCount = count + (hasAddSlide ? 1 : 0);
    // Clamp so a shrinking playlist (scene removed while open) can't strand us
    // past the end.
    const safeIndex = Math.min(index, Math.max(slotCount - 1, 0));
    const isAddSlide = hasAddSlide && safeIndex >= count;

    // Reset to the first scene each time the modal opens.
    useEffect(() => {
        if (opened) setIndex(0);
    }, [opened]);

    const goPrev = useCallback(
        (method: string) => {
            if (safeIndex <= 0) return;
            const next = safeIndex - 1;
            track(AnalyticsEvents.ScreenPreviewSceneChanged, { method, index: next });
            setIndex(next);
        },
        [safeIndex],
    );
    const goNext = useCallback(
        (method: string) => {
            if (safeIndex >= slotCount - 1) return;
            const next = safeIndex + 1;
            track(AnalyticsEvents.ScreenPreviewSceneChanged, { method, index: next });
            setIndex(next);
        },
        [safeIndex, slotCount],
    );

    // Desktop keyboard navigation.
    useEffect(() => {
        if (!opened) return undefined;
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'ArrowLeft') goPrev('keyboard');
            else if (e.key === 'ArrowRight') goNext('keyboard');
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [opened, goPrev, goNext]);

    const onTouchStart = (e: React.TouchEvent) => {
        touchStartX.current = e.touches[0]?.clientX ?? null;
    };
    const onTouchEnd = (e: React.TouchEvent) => {
        if (touchStartX.current === null) return;
        const delta = (e.changedTouches[0]?.clientX ?? 0) - touchStartX.current;
        if (delta <= -SWIPE_THRESHOLD_PX) goNext('swipe');
        else if (delta >= SWIPE_THRESHOLD_PX) goPrev('swipe');
        touchStartX.current = null;
    };

    const current = scenes[safeIndex];
    const atStart = safeIndex === 0;
    const atEnd = safeIndex >= slotCount - 1;

    return (
        <Modal
            opened={opened}
            onClose={onClose}
            fullScreen
            title={playlistName ?? 'Playlist preview'}
            styles={{ body: { height: 'calc(100% - 60px)', overflowY: 'auto' } }}
        >
            {count === 0 ? (
                <Text c="dimmed" ta="center" mt="xl">
                    This playlist has no scenes to preview yet.
                </Text>
            ) : (
                <Stack gap="sm" align="center" h="100%" justify="center">
                    <Text size="sm" c="dimmed">
                        {isAddSlide
                            ? 'Add a scene'
                            : current?.scene?.name ?? `Scene ${safeIndex + 1}`}
                    </Text>
                    <Group gap="md" align="center" wrap="nowrap" style={{ maxWidth: '100%' }}>
                        <ActionIcon
                            variant="default"
                            size="xl"
                            radius="xl"
                            aria-label="Previous scene"
                            onClick={() => goPrev('arrow')}
                            disabled={atStart}
                            visibleFrom="sm"
                        >
                            <IconChevronLeft />
                        </ActionIcon>

                        <Box
                            onTouchStart={onTouchStart}
                            onTouchEnd={onTouchEnd}
                            style={{
                                // Cap the 9:16 frame's width so it fits the viewport height
                                // *after* reserving room for the header + bottom controls
                                // (~240px). `svh` uses the small viewport height, so mobile
                                // browser chrome can't push the Edit button off-screen.
                                width: 'min(430px, 88vw, calc((100svh - 240px) * 9 / 16))',
                                touchAction: 'pan-y',
                                flex: '0 0 auto',
                                position: 'relative',
                            }}
                        >
                            {isAddSlide ? (
                                <UnstyledButton
                                    onClick={onAddScene}
                                    aria-label="Add scene"
                                    style={{
                                        width: '100%',
                                        aspectRatio: '9 / 16',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        gap: 12,
                                        border: '2px dashed var(--mantine-color-default-border)',
                                        borderRadius: 12,
                                        background: 'var(--mantine-color-default-hover)',
                                        color: 'var(--mantine-color-dimmed)',
                                    }}
                                >
                                    <IconPlus size={40} stroke={1.5} />
                                    <Text fw={600}>Add scene</Text>
                                </UnstyledButton>
                            ) : (
                                <>
                                    <ScenePreviewFrame
                                        variant="live"
                                        templateId={(current?.scene?.templateId ?? 'full') as SceneTemplateId}
                                        panels={(current?.scene?.panels ?? []).map(panel => ({
                                            componentId: panel.componentId,
                                            propOverrides: panel.propOverrides ?? [],
                                        }))}
                                        components={components}
                                    />
                                    {updating && (
                                        <Box
                                            style={{
                                                position: 'absolute',
                                                inset: 0,
                                                display: 'flex',
                                                flexDirection: 'column',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                gap: 10,
                                                background: 'rgba(0, 0, 0, 0.6)',
                                                borderRadius: 8,
                                                zIndex: 2,
                                            }}
                                        >
                                            <Loader size="sm" color="white" />
                                            <Text size="sm" c="white" fw={500}>
                                                Updating…
                                            </Text>
                                        </Box>
                                    )}
                                </>
                            )}
                        </Box>

                        <ActionIcon
                            variant="default"
                            size="xl"
                            radius="xl"
                            aria-label="Next scene"
                            onClick={() => goNext('arrow')}
                            disabled={atEnd}
                            visibleFrom="sm"
                        >
                            <IconChevronRight />
                        </ActionIcon>
                    </Group>

                    <Group gap={6} justify="center">
                        {Array.from({ length: slotCount }).map((_, i) => {
                            const isAddDot = hasAddSlide && i === count;
                            const dotColor = i === safeIndex ? activeDot : inactiveDot;
                            return (
                                <Box
                                    key={i}
                                    aria-hidden
                                    style={{
                                        width: 8,
                                        height: 8,
                                        borderRadius: '50%',
                                        // The add-slide dot is hollow so it reads as "one more, but not a scene".
                                        background: isAddDot ? 'transparent' : dotColor,
                                        border: isAddDot ? `1.5px solid ${dotColor}` : undefined,
                                    }}
                                />
                            );
                        })}
                    </Group>
                    {!isAddSlide && (
                        <Text size="xs" c="dimmed">
                            {safeIndex + 1} / {count}
                        </Text>
                    )}
                    <Text size="xs" c="dimmed" hiddenFrom="sm">
                        Swipe to change scenes
                    </Text>
                    {!isAddSlide && current && (
                        <Button
                            mt="xs"
                            variant="light"
                            leftSection={<IconPencil size="1rem" stroke={1.8} />}
                            onClick={() => onEditScene(current.sceneId)}
                        >
                            Edit scene
                        </Button>
                    )}
                </Stack>
            )}
        </Modal>
    );
};

export default PlaylistPreviewModal;
